import React, { useState, useEffect, useContext } from 'react';
import CategoryList from './CategoryList';
import CategoryCard from './CategoryCard';
import ProductCard from './ProductCard';
import './Shop.css';
import { LoadingContext } from '../LoadingContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Shop() {
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [displayCategories, setDisplayCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const { setIsLoading } = useContext(LoadingContext);
  const [isExpandedView] = useState(true);
  const [promoMap, setPromoMap] = useState({});

  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/categories/tree`);
        if (!response.ok) {
          throw new Error(`Failed to fetch categories: ${response.statusText}`);
        }
        const data = await response.json();
        setCategories(data);
        setAllCategories(data);
        setDisplayCategories(data);
      } catch (error) {
        // Silent fail (previously only 1 console.error line in this catch block, no other actions)
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();

    fetch(`${API_BASE_URL}/api/Promotions`)
      .then(r => r.ok ? r.json() : [])
      .then(promos => {
        const map = {};
        promos.forEach(p => {
          // Standalone promos are not catalogue products - they have no id to key on
          if (!p.productId && !p.variantId) return;
          map[p.variantId ? `v${p.variantId}` : `p${p.productId}`] = p.promotionPrice;
        });
        setPromoMap(map);
      })
      .catch(() => {});
  }, [setIsLoading]);

  // Function to search and filter
  const searchCategoriesAndProducts = async (term) => {
    setSearchTerm(term);
    setIsSearching(true);

    if (!term.trim()) {
      setCategories(allCategories);
      setDisplayCategories(allCategories);
      setProducts([]);
      setIsSearching(false);
      return;
    }

    // Search through category tree for sidebar
    const filterSidebarCategories = (categoriesArray, term) => {
      return categoriesArray.filter(category => {
        const nameMatch = category.name.toLowerCase().includes(term.toLowerCase());

        let filteredChildren = [];
        if (category.children && category.children.length > 0) {
          filteredChildren = filterSidebarCategories(category.children, term);
        }

        return nameMatch || filteredChildren.length > 0;
      }).map(category => {
        if (category.children && category.children.length > 0) {
          return {
            ...category,
            children: filterSidebarCategories(category.children, term)
          };
        }

        return category;
      });
    };

    // Collect all matching categories for display 
    const getAllMatchingCategories = (categoriesArray, term, results = []) => {
      for (const category of categoriesArray) {
        if (category.name.toLowerCase().includes(term.toLowerCase())) {
          results.push(category);
        }

        if (category.children && category.children.length > 0) {
          getAllMatchingCategories(category.children, term, results);
        }
      }
      return results;
    };

    const filteredSidebarCategories = filterSidebarCategories(allCategories, term);
    setCategories(filteredSidebarCategories);

    const allMatchingCategories = getAllMatchingCategories(allCategories, term);
    setDisplayCategories(allMatchingCategories);

    if (term.length >= 2) {
      try {
        setIsLoading(true);
        const productsResponse = await fetch(`${API_BASE_URL}/Products?SearchString=${encodeURIComponent(term)}`);
        if (!productsResponse.ok) {
          throw new Error(`Failed to fetch products: ${productsResponse.statusText}`);
        }
        const productsData = await productsResponse.json();
        setProducts(productsData.products || []);
      } catch (error) {
        setProducts([]);
      } finally {
        setIsLoading(false);
        setIsSearching(false);
      }
    } else {
      setProducts([]);
      setIsSearching(false);
    }
  };

  return (
    <div className="shop-main-container">
      <h2 className="shop-header">Our Products</h2>
      <div className="shop-container">
        <aside className={`shop-category-list-container ${!isExpandedView ? 'collapsed' : ''}`}>
          <div className="shop-category-list-top">
            <h3 className="shop-category-list-heading">Categories</h3>
          </div>

          {/* Search Bar Section
          {isExpandedView && (
            <div className="category-search-container" style={{ position: 'relative' }}>
              <input
                type="text"
                className="category-search-input"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => searchCategoriesAndProducts(e.target.value)}
                style={{ paddingLeft: '35px' }}
              />
              <svg
                style={{
                  position: 'absolute',
                  left: '15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#6c757d',
                  width: '18px',
                  height: '18px',
                  pointerEvents: 'none'
                }}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </div>
          )} */}

          {/* Category List Section */}
          {isExpandedView && (
            <CategoryList key="shop-category-list" categories={categories} />
          )}
        </aside>

        {/* Main Content Section */}
        <main className={`shop-category-card-container ${!isExpandedView ? 'expanded' : ''}`}>
          {isSearching ? (
            <div className="search-loading">Searching...</div>
          ) : searchTerm ? (

            <>
              {/* Categories Search Section */}
              {displayCategories.length > 0 && (
                <div className="shop-search-section">
                  <h3 className="shop-search-heading">Categories</h3>
                  <div className="shop-category-card-list">
                    {displayCategories.map(category => (
                      <CategoryCard key={category.categoryID} category={category} />
                    ))}
                  </div>
                </div>
              )}

              {/* Products Search Section */}
              {products.length > 0 && (
                <div className="shop-search-section">
                  <h3 className="shop-search-heading">Products</h3>
                  <div className="shop-product-card-list">
                    {products.map(product => (
                      <ProductCard key={product.productID} product={product} promoPrice={promoMap[`p${product.productID}`]} />
                    ))}
                  </div>
                </div>
              )}

              {/* No Results Message */}
              {displayCategories.length === 0 && products.length === 0 && (
                <div className="no-results-message">
                  <p>No products found matching your search.</p>
                  <p>Try a different search term or browse all categories.</p>
                </div>
              )}
            </>
          ) : (
            // Category Card Section (Default View)
            <div className="shop-category-card-list">
              {displayCategories.map(category => (
                <CategoryCard key={category.categoryID} category={category} />
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default Shop;